import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { logError } from "../pages/admin/adminUtils";

function useLoader(path, pick, label, params) {
  const [value, setValue] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(path, params ? { params } : undefined); setValue(pick(data)); }
    catch (e) { logError(`${label} failed`, e); toast.error(`Couldn't load ${label}. Please retry.`); }
  }, [path, pick, label, params]);
  return [value, load, setValue];
}

const same = (d) => d;
const pickRegs = (d) => d.registrations;
const pickLogs = (d) => d.logs;

export function useAdminData() {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const regParams = useMemo(() => ({ q, status: statusFilter }), [q, statusFilter]);
  const [stats, loadStats] = useLoader("/admin/stats", same, "overview stats");
  const [regs, loadRegs] = useLoader("/admin/registrations", pickRegs, "registrations", regParams);
  const [gifts, loadGifts] = useLoader("/admin/gifts", same, "jersey gifts");
  const [config, loadConfig, setConfig] = useLoader("/admin/config", same, "configuration");
  const [audit, loadAudit] = useLoader("/admin/audit", pickLogs, "the audit log");
  const [spons, loadSpons] = useLoader("/admin/sponsorships", same, "sponsorships");

  const search = () => loadRegs();
  const sponsorAction = async (ref, path, body) => {
    try { const { data } = await api.post(`/admin/sponsorships/${ref}/${path}`, body || {}); toast.success("Done"); loadSpons(); return data; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return null; }
  };

  return {
    stats, regs: regs || [], gifts, config, audit: audit || [], spons, setConfig,
    loadStats, loadRegs, loadGifts, loadConfig, loadAudit, loadSpons, sponsorAction,
    filters: { q, setQ, statusFilter, setStatusFilter, onSearch: search },
  };
}

export function useRegistrationActions(refresh) {
  const post = async (ref, path, prompt, okMsg) => {
    const remark = window.prompt(prompt, "");
    if (remark === null) return;
    try { await api.post(`/admin/registrations/${ref}/${path}`, { remark }); toast.success(okMsg); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (ref) => {
    if (!window.confirm(`Permanently delete registration ${ref}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/registrations/${ref}`); toast.success("Registration deleted"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return {
    confirm: (ref) => post(ref, "confirm-payment", "Optional remark for confirming this registration (why / who):", "Registration confirmed"),
    cancel: (ref) => post(ref, "cancel", "Reason for cancelling this registration:", "Registration cancelled"),
    annul: (ref) => post(ref, "annul", "Reason for annulling this registration:", "Registration annulled"),
    remove,
  };
}
